import { useMemo, useState, useCallback, useEffect, useRef } from "react"
import Window from "../Window/Window"
import styles from "./FinderWindow.module.css"
import { CaretLeft, CaretRight, MagnifyingGlass, List, SquaresFour, StackSimple, CloudArrowDown } from "phosphor-react"
import { getProjects, type Project } from "./projectsHelper"
import { useTranslationUpdate } from "../../hooks/useTranslationUpdate"
import { getCurrentLanguage, translations } from "../../translations"
import { useSound } from "../../hooks/useSound"
import { SOUNDS } from "../../constants/sounds"
import ProjectDetail from "./ProjectDetail"
import CatAlert from "./CatAlert"

type Props = { onClose: () => void; isMinimized: boolean; onToggleMinimize: () => void }
type OpenWin = { key: string; project: Project; minimized: boolean }

export default function FinderWindow({ onClose, isMinimized, onToggleMinimize }: Props) {
  useTranslationUpdate()
  const currentLang = getCurrentLanguage()
  const playClickFolder = useSound(SOUNDS.CLICK_FOLDER, { volume: 0.4 })

  const projects = useMemo(() => getProjects(currentLang), [currentLang])

  const files = useMemo(
    () => projects.map(p => ({ id: p.id, name: p.docName, icon: p.icon, folder: p.folder, project: p })),
    [projects]
  )
  const [openDocs, setOpenDocs] = useState<OpenWin[]>([])
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid")
  const [search, setSearch] = useState("")
  const [searchOpen, setSearchOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [history, setHistory] = useState<string[]>(["/Projetos"])
  const [hIndex, setHIndex] = useState(0)
  const currentPath = history[hIndex]
  const canBack = hIndex > 0
  const canForward = hIndex < history.length - 1

  const [showCat, setShowCat] = useState(false)

  const goBack = useCallback(() => { if (canBack) setHIndex(i => i - 1) }, [canBack])
  const goForward = useCallback(() => { if (canForward) setHIndex(i => i + 1) }, [canForward])
  const goTo = useCallback((path: string) => { setHistory(prev => [...prev.slice(0, hIndex + 1), path]); setHIndex(i => i + 1) }, [hIndex])

  useEffect(() => { setSearch("") }, [currentPath])
  useEffect(() => { if (searchOpen) inputRef.current?.focus() }, [searchOpen])
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === "Escape") { setSearchOpen(false); setSearch(""); setShowCat(false) } }
    window.addEventListener("keydown", k)
    return () => window.removeEventListener("keydown", k)
  }, [])

  useEffect(() => {
    setOpenDocs(prev => prev.map(doc => {
      const updatedProject = projects.find(p => p.id === doc.project.id)
      return updatedProject ? { ...doc, project: updatedProject } : doc
    }))
  }, [currentLang, projects])

  const visibleFiles = useMemo(() => {
    const base = files.filter(f => f.folder === currentPath)
    if (!search.trim()) return base
    const q = search.toLowerCase()
    return base.filter(f => f.name.toLowerCase().includes(q))
  }, [files, currentPath, search])

  const openProject = useCallback((p: Project) => {
    try {
      playClickFolder()
    } catch (err) {
      // Silent fail
    }

    const key = `${p.id}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
    setOpenDocs(prev => [...prev, { key, project: p, minimized: false }])
  }, [playClickFolder])

  const closeWin = (key: string) => setOpenDocs(prev => prev.filter(w => w.key !== key))
  const toggleMin = (key: string) => setOpenDocs(prev => prev.map(w => (w.key === key ? { ...w, minimized: !w.minimized } : w)))

  if (isMinimized) return null

  return (
    <>
      <Window
        title={translations('finder.title')}
        onClose={onClose}
        isMinimized={isMinimized}
        onToggleMinimize={onToggleMinimize}
        initialPosition={{ x: 140, y: 40 }}
        initialSize={{ width: 980, height: 640 }}
        minSize={{ width: 720, height: 460 }}
        bodyClassName={styles.body}
        containerClassName={styles.poppinsAll}
      >
        <div className={styles.layout}>
          <aside className={`${styles.sidebar} ${styles.sidebarMac}`}>
            <div className={styles.sectionTitle}>{translations('finder.favorites')}</div>

            <button className={`${styles.sideItem} ${currentPath === "/Projetos" ? styles.sideItemActive : ""}`} onClick={() => goTo("/Projetos")} aria-label={translations('finder.projects')}>
              <StackSimple size={20} weight="duotone" className={styles.sideIcon} />
              <span className={styles.sideLabel}>{translations('finder.projects')}</span>
            </button>

            <button className={`${styles.sideItem} ${currentPath === "/Downloads" ? styles.sideItemActive : ""}`} onClick={() => goTo("/Downloads")} aria-label={translations('finder.downloads')}>
              <CloudArrowDown size={20} weight="duotone" className={styles.sideIcon} />
              <span className={styles.sideLabel}>{translations('finder.downloads')}</span>
            </button>
          </aside>

          <main className={styles.content}>
            <div className={styles.toolbar}>
              <div className={styles.navGroup}>
                <button className={styles.iconBtn} onClick={goBack} disabled={!canBack} aria-label={translations('finder.back')}><CaretLeft size={18} /></button>
                <button className={styles.iconBtn} onClick={goForward} disabled={!canForward} aria-label={translations('finder.forward')}><CaretRight size={18} /></button>
              </div>
              <div className={styles.spacer} />
              <button className={styles.iconBtn} onClick={() => setViewMode(viewMode === "grid" ? "list" : "grid")} aria-label={viewMode === "grid" ? translations('finder.list_view') : translations('finder.grid_view')} title={viewMode === "grid" ? translations('finder.list') : translations('finder.grid')}>{viewMode === "grid" ? <List size={18} /> : <SquaresFour size={18} />}</button>
              {!searchOpen ? (
                <button className={styles.iconBtn} aria-label={translations('finder.search')} onClick={() => setSearchOpen(true)} title={translations('finder.search')}><MagnifyingGlass size={18} /></button>
              ) : (
                <div className={`${styles.searchWrap} ${styles.searchWrapOpen}`}>
                  <MagnifyingGlass size={16} />
                  <input ref={inputRef} className={styles.searchInput} placeholder={translations('finder.search')} value={search} onChange={e => setSearch(e.target.value)} onBlur={() => { if (!search) setSearchOpen(false) }} />
                </div>
              )}
            </div>

            {viewMode === "grid" ? (
              <div className={styles.grid}>
                {visibleFiles.map(f => (
                  <button key={f.id} className={styles.file} onClick={() => openProject(f.project)} aria-label={f.name} title={f.name}>
                    <img src={f.icon} className={styles.fileIcon} alt="" />
                    <span className={styles.fileLabel}>{f.name}</span>
                  </button>
                ))}

                {currentPath === "/Downloads" && (
                  <button
                    className={styles.file}
                    onClick={() => setShowCat(true)}
                    aria-label="???.err"
                    title="???.err"
                  >
                    <img src="/images/icons/file-doc.png" className={styles.fileIcon} alt="" />
                    <span className={styles.fileLabel}>???.err</span>
                  </button>
                )}
              </div>
            ) : (
              <div className={styles.list}>
                {visibleFiles.map(f => (
                  <button key={f.id} className={styles.listRow} onClick={() => openProject(f.project)} title={f.name} aria-label={f.name}>
                    <img src={f.icon} className={styles.listIcon} alt="" />
                    <span className={styles.listName}>{f.name}</span>
                  </button>
                ))}
              </div>
            )}
          </main>
        </div>

        {showCat && <CatAlert onClose={() => setShowCat(false)} />}
      </Window>

      {openDocs.map((w, i) => (
        <Window
          key={w.key}
          title={w.project.docName}
          onClose={() => closeWin(w.key)}
          isMinimized={w.minimized}
          onToggleMinimize={() => toggleMin(w.key)}
          initialPosition={{ x: 220 + 24 * i, y: 90 + 24 * i }}
          initialSize={{ width: 900, height: 620 }}
          minSize={{ width: 720, height: 540 }}
          bodyClassName={styles.docBody}
        >
          <ProjectDetail
            logo={w.project.logo}
            title={w.project.title}
            about={w.project.about}
            technologies={w.project.technologies}
            photos={w.project.photos}
            tags={w.project.tags}
            primary={w.project.primary}
            secondary={w.project.secondary}
            link={w.project.link}
          />
        </Window>
      ))}
    </>
  )
}